import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Listen for slide change on the slideshow component
    this.slideshow?.addEventListener('change', this.#handleSlideChange, { signal });
    this.slideshow?.addEventListener('slideshow:select', this.#handleSlideChange, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Pauses all HTML5 videos, deferred media elements, and iframe players inside the gallery.
   */
  #pauseAllMedia() {
    // 1. Pause HTML5 standard <video> elements
    this.querySelectorAll('video').forEach((video) => {
      video.pause();
    });

    // 2. Pause/Reset Shopify <deferred-media> elements
    this.querySelectorAll('deferred-media').forEach((deferredMedia) => {
      if (typeof deferredMedia.pause === 'function') {
        deferredMedia.pause();
      }
      
      // Pause iframe players (YouTube / Vimeo)
      const iframe = deferredMedia.querySelector('iframe');
      if (iframe) {
        iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
      }
    });
  }

  /**
   * Handles slide change events to stop background videos and play active media.
   */
  #handleSlideChange = (event) => {
    this.#pauseAllMedia();

    const activeIndex = event.detail?.index ?? this.slideshow?.selectedIndex;
    if (activeIndex === undefined) return;

    const currentMedia = this.refs.media?.[activeIndex];
    if (!currentMedia) return;

    // Automatically play video if the newly selected slide is a video
    const video = currentMedia.querySelector('video');
    if (video) {
      video.play().catch(() => {
        // Autoplay with audio might be blocked by browser policy
      });
    }
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}